/* eslint-disable import/no-cycle */
import React, { createRef, useEffect, useReducer } from "react"
import { useIntl, defineMessages } from "react-intl"
import _ from "lodash"
import Typography from "@material-ui/core/Typography"
import WarningIcon from "@material-ui/icons/Warning"
import makeStyles from "@material-ui/core/styles/makeStyles"
import Box from "@material-ui/core/Box"
import CodeIcon from "@material-ui/icons/Code"
import WidgetsIcon from "@material-ui/icons/Widgets"
import IconButton from "@material-ui/core/IconButton"
import PropTypes from "prop-types"
import Tooltip from "@material-ui/core/Tooltip"
import Button from "@material-ui/core/Button"
import Api from "../../../api/Api"
import WidgetEditorModal from "./WidgetEditorModal"
import { pages } from "../../../widgets/pages"
import { PRIVATE } from "../../../widgets/scopes"
import { WIDGET_TYPES } from "../../../widgets/types"
import { WidgetSubscriptionsTypes } from "./WidgetPropTypes"
import SkeletonInfoCard from "./SkeletonInfoCard"
import CircularProgressBox from "../../../component/CircularProgressBox"
import { CancelToken } from "../../../api/httpClient"
import { WIDGET_SEPARATION_MODES } from "../../../widgets/separationModes"
import { defaultParams } from "../../../widgets/defaultParams"

const useStyles = makeStyles((theme) => ({
  skeleton: {
    padding: theme.spacing(2),
    margin: theme.spacing(0.6),
    backgroundColor: "rgba(43, 43, 43, 0.11)"
  }
}))

const msgIntlDesc = defineMessages({
  title: {
    id: "widget.skeleton.title",
    defaultMessage: "Here your widget"
  },
  edit: {
    id: "widget.skeleton.edit.widget",
    defaultMessage: "Edit widget"
  },
  copy: {
    id: "widget.skeleton.copy.json",
    defaultMessage: "Copty widget to JSON"
  },
  noTitle: {
    id: "widget.skeleton.no.title",
    defaultMessage: "No title"
  },
  noSubscription: {
    id: "widget.skeleton.no.subscription",
    defaultMessage: "No events subscriptions (date, portfolio, customer)"
  },
  notFoundWidget: {
    id: "widget.not.found",
    defaultMessage: "widget not found"
  },
  hereTheWidget: {
    id: "widget.here.widget",
    defaultMessage: "Here your widget"
  },
  buttonNew: {
    id: "button.new",
    defaultMessage: "New"
  }
})

const copyWidgetJSONToClipboard = (textAreaRef) => {
  // select input to used copy command
  textAreaRef.current.select()
  document.execCommand("copy")
}

/**
 * Initial local component state.
 */
const initialState = {
  widgetData: {},
  widgetViewModel: {},
  loading: true,
  isEditionOpen: false,
  reloadFlag: false
}

/**
 * Events used by local dispatcher from reducer.
 */
const events = {
  apiResponseEvent: (widgetData, widgetViewModel) => ({
    type: "API_RESPONSE_EVENT",
    payload: { widgetData, widgetViewModel }
  }),
  endLoadingEvent: () => ({ type: "END_LOADING_EVENT" }),
  editWidgetEvent: () => ({ type: "EDIT_WIDGET_EVENT" }),
  closedEditionEvent: () => ({ type: "EDIT_WIDGET_CLOSED_EVENT" })
}

/**
 * Local reducer function.
 */
const localReducer = (state, action) => {
  switch (action.type) {
    case "API_RESPONSE_EVENT":
      return _.assign({}, state, action.payload)
    case "END_LOADING_EVENT":
      return _.assign({}, state, { loading: false })
    case "EDIT_WIDGET_EVENT":
      return _.assign({}, state, { isEditionOpen: true })
    case "EDIT_WIDGET_CLOSED_EVENT":
      return _.assign({}, state, {
        loading: true,
        isEditionOpen: false,
        reloadFlag: !state.reloadFlag
      })
    default:
      throw new Error(`Not managed action type: ${action.type}`)
  }
}

const createViewModel = (data, subscriptions, format, title, widgetId) => {
  const titleOrDefault = title || format(msgIntlDesc.noTitle)
  if (data) {
    // it's an existing widget
    return {
      id: data._id,
      type: data.__t,
      dashboard: data.dashboard,
      alias: data.alias,
      title: titleOrDefault
    }
  }

  //
  return {
    alias: widgetId,
    title: titleOrDefault
  }
}

const fromDataOrDefault = (data, widgetId, page) => {
  if (data) {
    // when data, return this with stringify defaultParams
    return _.assign(data, {
      defaultParams: JSON.stringify(data.defaultParams, null, "\t")
    })
  }

  return {
    alias: widgetId,
    screen: page,
    scope: PRIVATE,
    type: WIDGET_TYPES.DASHBOARD,
    separationMode: WIDGET_SEPARATION_MODES.SAME_WIDGET_FOR_ALL,
    widgetsByVisitorProfile: [],
    defaultParams
  }
}

/**
 * A widget skeleton component to see widget information when configuring widgets.
 */
export default function WidgetSkeleton(props) {
  const { title, widgetId, subscriptions, page } = props

  const classes = useStyles()
  const { formatMessage: format } = useIntl()
  const [state, dispatch] = useReducer(localReducer, initialState)

  // a ref to store input value of JSON data.
  const widgetJsonRef = createRef()

  const isWidgetExist = () => state.widgetData.id

  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    Api.request(`/widgets/${widgetId}`, null, token)
      .then((data) => {
        // create a view model from model
        const viewModel = createViewModel(
          data,
          subscriptions,
          format,
          title,
          widgetId
        )

        // serialize defaultParms into string for EditModal form
        const widget = fromDataOrDefault(data, widgetId, page)
        dispatch(events.apiResponseEvent(widget, viewModel))
      })
      .finally(() => dispatch(events.endLoadingEvent()))
    return () => cancel()
  }, [subscriptions, title, widgetId, state.reloadFlag, format, page])

  // It's not in referential, display an error in the widget skeleton
  const skeletonProps = {
    widgetData: state.widgetData,
    widgetViewModel: state.widgetViewModel,
    inputRef: widgetJsonRef
  }

  const renderErrorSkeleton = () => (
    <SkeletonInfoCard
      className={`${classes.skeleton} skeleton-warning`}
      {...skeletonProps}
    >
      <Box display="flex" alignItems="center">
        <Box className="skeleton-icon">
          <WarningIcon className={classes.icon} />
        </Box>
        <Typography className="skeleton-title" variant="h5">
          {format(msgIntlDesc.notFoundWidget)}
        </Typography>
        <Button
          onClick={() => dispatch(events.editWidgetEvent())}
          variant="outlined"
          color="secondary"
        >
          {format(msgIntlDesc.buttonNew)}
        </Button>
      </Box>
    </SkeletonInfoCard>
  )

  const renderWithDataSkeleton = () => (
    <SkeletonInfoCard
      className={`${classes.skeleton} infosHeader`}
      {...skeletonProps}
    >
      <Box display="flex" alignItems="center">
        <Typography variant="h5" className="skeleton-title">
          {format(msgIntlDesc.hereTheWidget)}
        </Typography>
        <IconButton
          color="primary"
          aria-label={format(msgIntlDesc.edit)}
          component="span"
          onClick={() => dispatch(events.editWidgetEvent())}
        >
          <Tooltip title={format(msgIntlDesc.edit)}>
            <WidgetsIcon className={classes.icon} />
          </Tooltip>
        </IconButton>
        <IconButton
          color="primary"
          aria-label={format(msgIntlDesc.copy)}
          component="span"
          onClick={() => copyWidgetJSONToClipboard(widgetJsonRef)}
        >
          <Tooltip title={format(msgIntlDesc.copy)}>
            <CodeIcon className={classes.icon} />
          </Tooltip>
        </IconButton>
      </Box>
    </SkeletonInfoCard>
  )

  const renderSkeleton = isWidgetExist()
    ? renderWithDataSkeleton
    : renderErrorSkeleton

  // It's in the referential, display info about it
  return (
    <>
      {/* Widget popal should be first */}
      <WidgetEditorModal
        isOpen={state.isEditionOpen}
        onClose={() => dispatch(events.closedEditionEvent())}
        widget={state.widgetData}
      />

      {/* if is loading, then render loader else render skeleton */}
      {(state.loading && <CircularProgressBox height="120px" />) ||
        renderSkeleton()}
    </>
  )
}

WidgetSkeleton.propTypes = {
  title: PropTypes.string,
  widgetId: PropTypes.string.isRequired,
  subscriptions: WidgetSubscriptionsTypes,
  page: PropTypes.oneOf(pages).isRequired
}

WidgetSkeleton.defaultProps = {
  title: undefined,
  subscriptions: undefined
}
